/**
 * Typed tenant floor plan API facade over the generated contract client.
 */

import { locationFloorPlansApi } from '../api/generated/locationFloorPlans.api.js';
import {
  FloorPlanUploadFormSchema,
  type AnchorPointAssignmentBody,
  type AnchorPointResponse,
  type FloorPlanDeleteResponse,
  type FloorPlanImageUrlResponse,
  type FloorPlanListResponse,
  type FloorPlanResponse,
  type FloorPlanUpdateBody,
} from '@cnapse/shared/schemas.js';

class LocationFloorPlansService {
  async getFloorPlans(siteId: string): Promise<FloorPlanListResponse> {
    return locationFloorPlansApi.listFloorPlans({ siteId }) as Promise<FloorPlanListResponse>;
  }

  async getFloorPlanImageUrl(siteId: string, floorId: string): Promise<FloorPlanImageUrlResponse> {
    return locationFloorPlansApi.getFloorPlanImageUrl({ siteId, floorId }) as Promise<FloorPlanImageUrlResponse>;
  }

  async uploadFloorPlan(siteId: string, formData: FormData): Promise<FloorPlanResponse> {
    const body = FloorPlanUploadFormSchema.parse({
      image: formData.get('image'),
      displayName: formData.get('displayName'),
      isDefault: formData.get('isDefault'),
      floorId: formData.get('floorId') ?? undefined,
      coordinateScale: formData.get('coordinateScale') ?? undefined,
      originOffsetX: formData.get('originOffsetX') ?? undefined,
      originOffsetY: formData.get('originOffsetY') ?? undefined,
      layerId: formData.get('layerId') ?? undefined,
    });
    return locationFloorPlansApi.uploadFloorPlan({ siteId }, body) as Promise<FloorPlanResponse>;
  }

  async updateFloorPlan(
    siteId: string,
    floorId: string,
    body: FloorPlanUpdateBody,
  ): Promise<FloorPlanResponse> {
    return locationFloorPlansApi.updateFloorPlan({ siteId, floorId }, body) as Promise<FloorPlanResponse>;
  }

  async deleteFloorPlan(siteId: string, floorId: string): Promise<FloorPlanDeleteResponse> {
    return locationFloorPlansApi.deleteFloorPlan({ siteId, floorId }) as Promise<FloorPlanDeleteResponse>;
  }

  async updateAnchorPointAssignment(
    siteId: string,
    floorId: string,
    anchorPointId: string,
    body: AnchorPointAssignmentBody,
  ): Promise<AnchorPointResponse> {
    return locationFloorPlansApi.updateAnchorPointAssignment(
      { siteId, floorId, anchorPointId },
      body,
    ) as Promise<AnchorPointResponse>;
  }
}

export const locationFloorPlansService = new LocationFloorPlansService();
